import { Provider as MobxProvider } from 'mobx-react';
import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';

import { CacheProvider } from '@emotion/react';
import { loadableReady } from '@loadable/component';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

import createEmotionCache from 'app/create-emotion-cache';
import { createStores } from 'app/store';
import { ConfigsProvider } from 'main/modules/configs';
import { ElasticSearchProvider } from 'main/modules/elastic';
import I18n from 'main/theme/i18n';
import OrangeTheme from 'main/theme/OrangeTheme';

import App from './App';
import SnackBarProvider from './main/pages/components/SnackBarProvider';

import './scss/main.scss';

const hostnameToEnvironmentMap = {
    ['beta.vulners.com']: 'unstable',
    ['alpha.vulners.com']: 'testing',
    ['vulners.com']: 'production'
};

const environment = hostnameToEnvironmentMap[window.location.hostname] || 'development';

if (environment === 'production' || environment === 'unstable') {
    Sentry.init({
        dsn: 'https://afff0c0b9fea4021b656ee39a079ba04@sentry-public.vulners.com//8',
        integrations: [new BrowserTracing()],
        environment,
        // We recommend adjusting this value in production, or using tracesSampler
        // for finer control
        tracesSampleRate: 1.0
    });
}

const { mobxStores, bootstrap } = createStores(window._____APP_STATE_____);
document.i18n = window.i18n = I18n.get;

const cache = createEmotionCache();

/**
 * Run App
 */
loadableReady(() => {
    const method = window.__SSR__ ? 'hydrate' : 'render';

    function Main() {
        React.useLayoutEffect(() => {
            bootstrap();
        }, []);

        return (
            <StyledEngineProvider injectFirst>
                <CacheProvider value={cache}>
                    <ThemeProvider theme={OrangeTheme}>
                        <ConfigsProvider value={window.__CONFIG__}>
                            <MobxProvider {...mobxStores}>
                                <ElasticSearchProvider>
                                    <SnackBarProvider>
                                        <BrowserRouter>
                                            <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
                                                <App />
                                            </Sentry.ErrorBoundary>
                                        </BrowserRouter>
                                    </SnackBarProvider>
                                </ElasticSearchProvider>
                            </MobxProvider>
                        </ConfigsProvider>
                    </ThemeProvider>
                </CacheProvider>
            </StyledEngineProvider>
        );
    }

    ReactDOM[method](<Main />, document.getElementById('body'));
});
